import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSnackbar } from 'notistack';
import SubscriptionsList from './components/SubscriptionsList';
import SubscriptionUserListings from './components/SubscriptionUserListings';
import AddSubscriptionModal from './components/AddSubscriptionModal';
import EditSubscriptionModal from './components/EditSubscriptionModal';
import DeleteSubscriptionModal from './components/DeleteSubscriptionModal';
import {
  ISubscription,
  SubscriptionPayload,
} from '@/types/subscription-management.types';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { usePermissions } from '@/hooks/usePermissions';

const SubscriptionManagement: React.FC = () => {
  const { hasPermission } = usePermissions();
  const getErrorMessage = (error: unknown, fallback: string): string => {
    const e = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    return e?.response?.data?.message || e?.message || fallback;
  };
  const { enqueueSnackbar } = useSnackbar();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<ISubscription | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] =
    useState<ISubscription | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showUserListings, setShowUserListings] = useState(false);

  const handleOpenAddModal = () => setIsAddModalOpen(true);
  const handleCloseAddModal = () => setIsAddModalOpen(false);

  const handleEdit = (subscription: ISubscription) => {
    setSelectedSubscription(subscription);
    setIsEditModalOpen(true);
  };
  const handleCloseEdit = () => {
    setIsEditModalOpen(false);
    setSelectedSubscription(null);
  };

  const handleDelete = (subscription: ISubscription) => {
    setSubscriptionToDelete(subscription);
    setIsDeleteModalOpen(true);
  };
  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setSubscriptionToDelete(null);
  };

  const handleCreate = async (payload: SubscriptionPayload) => {
    if (!payload.name.trim()) {
      enqueueSnackbar('Subscription name is required', { variant: 'error' });
      return;
    }
    try {
      setIsCreating(true);
      await SubscriptionsService.createSubscription(payload);
      enqueueSnackbar('Subscription created successfully', {
        variant: 'success',
      });
      handleCloseAddModal();
      setRefreshTrigger((v) => v + 1);
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to create subscription'
      );
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdate = async (id: string, payload: SubscriptionPayload) => {
    if (!payload.name.trim()) {
      enqueueSnackbar('Subscription name is required', { variant: 'error' });
      return;
    }
    try {
      setIsUpdating(true);
      await SubscriptionsService.updateSubscription(id, payload);
      enqueueSnackbar('Subscription updated successfully', {
        variant: 'success',
      });
      handleCloseEdit();
      setRefreshTrigger((v) => v + 1);
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to update subscription'
      );
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!subscriptionToDelete) return;
    try {
      setIsDeleting(true);
      await SubscriptionsService.deleteSubscription(subscriptionToDelete.id);
      enqueueSnackbar('Subscription deleted successfully', {
        variant: 'success',
      });
      setIsDeleteModalOpen(false);
      setSubscriptionToDelete(null);
      setRefreshTrigger((v) => v + 1);
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to delete subscription'
      );
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Subscription Management</title>
      </Helmet>
      <div
        className="container-fixed mx-auto"
        role="region"
        aria-labelledby="subscriptions-title"
      >
        <div className="subscription-management-header flex justify-between items-center mb-6">
          <h1
            id="subscriptions-title"
            className="text-xl font-medium leading-none text-gray-900"
            tabIndex={-1}
          >
            Subscriptions
          </h1>
          <div
            className="flex items-center gap-2"
            role="toolbar"
            aria-label="Subscription actions"
          >
            {hasPermission('subscription_management_read') && (
              <button
                className="btn btn-sm btn-light"
                onClick={() => setShowUserListings((v) => !v)}
                aria-label={
                  showUserListings
                    ? 'Back to Subscriptions'
                    : 'View Details by Plan'
                }
                aria-pressed={showUserListings}
              >
                {showUserListings
                  ? 'Back to Subscriptions'
                  : 'View Details by Plan'}
              </button>
            )}
            {hasPermission('subscription_management_create') &&
              !showUserListings && (
                <button
                  className="btn btn-sm btn-light"
                  onClick={handleOpenAddModal}
                  aria-label="Add new subscription"
                >
                  Add New Subscription
                </button>
              )}
          </div>
        </div>

        {showUserListings ? (
          <SubscriptionUserListings refreshTrigger={refreshTrigger} />
        ) : (
          <SubscriptionsList
            onEdit={
              hasPermission('subscription_management_update')
                ? handleEdit
                : undefined
            }
            onDelete={
              hasPermission('subscription_management_delete')
                ? handleDelete
                : undefined
            }
            refreshTrigger={refreshTrigger}
            canEdit={hasPermission('subscription_management_update')}
            canDelete={hasPermission('subscription_management_delete')}
            canManagePermissions={hasPermission(
              'subscription_management_update'
            )}
          />
        )}
      </div>

      <AddSubscriptionModal
        isOpen={isAddModalOpen}
        isCreating={isCreating}
        onClose={handleCloseAddModal}
        onCreate={handleCreate}
      />

      <DeleteSubscriptionModal
        isOpen={isDeleteModalOpen}
        isDeleting={isDeleting}
        subscription={subscriptionToDelete}
        onClose={handleCancelDelete}
        onConfirmDelete={handleConfirmDelete}
      />

      <EditSubscriptionModal
        isOpen={isEditModalOpen}
        isUpdating={isUpdating}
        subscription={selectedSubscription}
        onClose={handleCloseEdit}
        onUpdate={handleUpdate}
      />
    </>
  );
};

export default SubscriptionManagement;
